import ReactMarkdown from 'react-markdown'

import { InlineCode } from '@shared/ui'
import Link from 'next/link'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { ChatCodeSnippet } from '../ChatCodeSnippet'
import {
  CWTable,
  CWTableHeadCell,
  CWTableBodyRow,
  CWTableBodyCell,
  CWTableHead,
  CWTableBody,
} from '../ComponentsWeb/Table'
import { TextLink } from '../TextLink'
import { UnorderedList } from '../UnorderedList'

import { extractCode } from './extractCode/extractCode'
import { extractLanguage } from './extractLanguage/extractLanguage'

import type { SupportedLanguages } from '@circlefin/components/lib/CodeSnippet'
import type { Components } from 'react-markdown/lib'

/**
 * Props for the MarkdownRenderer component.
 */
export type MarkdownRendererProps = {
  /**
   * Additional classes.
   */
  className?: string

  /**
   * Markdown string.
   */
  markdown?: string | null
  /**
   * Whether to use codegen components.
   */
  enableChatbotComponents?: boolean
}

/**
 * Component that receives markdown as a prop and renders html.
 */
export const MarkdownRenderer = ({
  markdown,
  enableChatbotComponents = false,
  className,
}: MarkdownRendererProps) => {
  const chatbotComponents: Components = {
    code: ({ children, ...props }) => (
      <InlineCode {...props}>{children}</InlineCode>
    ),
    pre: ({ children, ...props }) => {
      if (children == null) return null

      const language = extractLanguage({ children })
      if (language == null) return <pre {...props}>{children}</pre>

      return (
        <ChatCodeSnippet
          content={extractCode({ children })}
          language={language as SupportedLanguages} // Need to reconcile SupportedLanguages and LanguageType types
          {...props}
        />
      )
    },
  }

  const components = {
    table: CWTable,
    th: CWTableHeadCell,
    tr: CWTableBodyRow,
    td: CWTableBodyCell,
    thead: CWTableHead,
    tbody: CWTableBody,
    ul: UnorderedList,
    a: (props) => (
      <TextLink
        {...props}
        anchorComponent={Link}
        href={props.href ?? ''}
        variant="primary"
      />
    ),
    code: (props) => (
      <code
        className="whitespace-pre-wrap"
        data-testid="default-code-element"
        {...props}
      />
    ),
    ...(enableChatbotComponents ? chatbotComponents : {}),
  } as Components

  return (
    <div className={className}>
      <ReactMarkdown
        components={components}
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  )
}
